const ALLOWED_UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

/** 허용된 UTM 파라미터 키. 값과 타입이 `ALLOWED_UTM_PARAMS` 한 곳에서 나온다 */
export type UtmParam = (typeof ALLOWED_UTM_PARAMS)[number];

const ALLOWED_UTM_PARAM_SET: ReadonlySet<string> = new Set(ALLOWED_UTM_PARAMS);

export const isUtmParam = (key: string): boolean =>
  ALLOWED_UTM_PARAM_SET.has(key);

/**
 * URL에서 UTM 파라미터만 추출하여 안전한 URL을 생성합니다.
 * @param url - 필터링할 URL (기본값: 현재 페이지 URL)
 * @param options.includeUtm - UTM 파라미터 포함 여부 (기본값: true)
 * @returns origin + pathname + UTM 쿼리 파라미터
 */
export const getSafeReferrerUrl = (
  url?: string,
  options: { includeUtm?: boolean } = {},
): string => {
  const { includeUtm = true } = options;
  const targetUrl = url || window.location.href;
  const urlObj = new URL(targetUrl);

  if (!includeUtm) {
    return urlObj.origin + urlObj.pathname;
  }

  const safeParams = new URLSearchParams();
  urlObj.searchParams.forEach((value, key) => {
    if (isUtmParam(key)) {
      safeParams.set(key, value);
    }
  });

  const paramString = safeParams.toString();
  return (
    urlObj.origin + urlObj.pathname + (paramString ? `?${paramString}` : '')
  );
};

/**
 * 공유용 URL — 방문자가 타고 들어온 UTM 은 버리고 공유 UTM 만 새로 붙인다.
 *
 * 기존 UTM 을 남기면 공유한 사람의 유입 캠페인이 받는 사람 세션에 승계돼
 * 원래 캠페인 성과가 부풀려진다. 해시도 함께 사라진다 — "이 아티클 공유하기"는
 * 읽던 섹션이 아니라 글을 공유하는 동작이다.
 */
export const buildShareUrl = (
  utm: Partial<Record<UtmParam, string>>,
  url?: string,
): string => {
  const shareUrl = new URL(getSafeReferrerUrl(url, { includeUtm: false }));

  Object.entries(utm).forEach(([key, value]) => {
    if (value !== undefined) shareUrl.searchParams.set(key, value);
  });

  return shareUrl.toString();
};

/**
 * 3billion 소유 도메인 여부. endsWith('.3billion.io') 로 서브도메인만 허용해
 * 'evil-3billion.io' 같은 위장 도메인을 internal 로 오분류하지 않는다.
 * gebra.io 는 자사 GEBRA 제품 도메인이라 자사로 취급한다(referrer 보존 목적).
 */
export const isOwnDomain = (hostname: string): boolean =>
  hostname === '3billion.io' ||
  hostname.endsWith('.3billion.io') ||
  hostname === 'gebra.io' ||
  hostname.endsWith('.gebra.io');

/**
 * 새 탭(target="_blank") 링크의 rel 값을 결정한다.
 * - 자사 도메인/상대경로 → 'noopener' (referrer 유지 → 유입 추적 연속성)
 * - 외부 도메인 → 'noopener noreferrer' (referrer 제거)
 * (raw <a> 용. TrackedAnchor 는 자체 getReferrerValue 로 처리한다.)
 */
export const getNewTabRel = (href: string): string => {
  // relative path / fragment 는 same-origin → 자사 취급
  if (!href || /^[/#.]/.test(href)) return 'noopener';
  try {
    return isOwnDomain(new URL(href).hostname)
      ? 'noopener'
      : 'noopener noreferrer';
  } catch {
    return 'noopener noreferrer';
  }
};
